import React, { CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import LeaderboardNameModal from '../common/leaderboardNameModal';
import {
  getPlayerNameValidationMessage,
  getSavedPlayerName,
  submitGameHighscore,
} from '../../../services/leaderboardService';
import pacmanMapData from '../pacman-react-master/src/mapData/pacman6.json';
import './pacman.css';

const audioFile = require('./pacmanTheme.mp3');
const spriteSheet = require('../pacman-react-master/src/resources/arcade_pacman_sprites.png');

const TILE_SIZE = 20;
const BOARD_TILES_WIDTH = 28;
const BOARD_TILES_HEIGHT = 31;
const BOARD_WIDTH = BOARD_TILES_WIDTH * TILE_SIZE;
const SPRITE_SCALE = 2.5;
const STEP_MS = 16;
const PACMAN_STEP = 2;
const GHOST_STEP_NORMAL = 2;
const GHOST_STEP_FRIGHTENED = 1;
const GHOST_STEP_DEAD = 3;
const POWER_MODE_DURATION = 5000;
const SPAWN_PROTECTION_DURATION = 1800;
const SCATTER_DURATION = 7000;
const CHASE_DURATION = 20000;
const HOME_TILE = { x: 14, y: 14 };

const BASIC_PILL_ID = 3533;
const ENERGIZER_ID = 3589;
const WAY_FREE_ID = 5240;
const BOX_DOOR_ID = 5241;

type Direction = 'UP' | 'DOWN' | 'LEFT' | 'RIGHT';
type GhostMode = 'scatter' | 'chase';
type GhostState = GhostMode | 'frightened' | 'dead';

interface Position {
  x: number;
  y: number;
}

interface Ghost {
  id: number;
  position: Position;
  direction: Direction;
  state: GhostState;
  previousMode: GhostMode;
  color: string;
  scatterTarget: Position;
}

interface GameState {
  started: boolean;
  paused: boolean;
  gameOver: boolean;
  gameWon: boolean;
  score: number;
  lives: number;
  powerModeRemaining: number;
  mode: GhostMode;
  modeTimer: number;
  animationTick: number;
  spawnProtectionMs: number;
  pacman: {
    position: Position;
    direction: Direction;
    nextDirection: Direction;
  };
  ghosts: Ghost[];
  pills: number[][];
  pelletsLeft: number;
}

interface PacManGameProps {
  onClose?: () => void;
  onReload?: () => void;
  onLeaderboardUpdated?: () => void;
}

type MapLayer = {
  name: string;
  data: number[];
  width: number;
  height: number;
};

type MazeMap = {
  layers: MapLayer[];
};

const mapData = pacmanMapData as MazeMap;

function getLayer(name: string): MapLayer {
  const layer = mapData.layers.find((item) => item.name === name);
  if (!layer) {
    throw new Error(`Layer ${name} not found`);
  }
  return layer;
}

function toMatrix(data: number[], width: number, height: number): number[][] {
  const matrix: number[][] = [];
  for (let y = 0; y < height; y++) {
    const row: number[] = [];
    for (let x = 0; x < width; x++) {
      row.push(data[y * width + x] ?? 0);
    }
    matrix.push(row);
  }
  return matrix;
}

const waysLayer = getLayer('Ways');
const pillsLayer = getLayer('Pills');
const waysMatrix = toMatrix(waysLayer.data, waysLayer.width, waysLayer.height);
const initialPillsMatrix = toMatrix(pillsLayer.data, pillsLayer.width, pillsLayer.height);

function cloneMatrix(matrix: number[][]): number[][] {
  return matrix.map((row) => row.slice());
}

function countPellets(matrix: number[][]): number {
  let count = 0;
  for (const row of matrix) {
    for (const value of row) {
      if (value === BASIC_PILL_ID || value === ENERGIZER_ID) {
        count += 1;
      }
    }
  }
  return count;
}

function tileFromScreen(position: Position): Position {
  return {
    x: Math.floor(position.x / TILE_SIZE),
    y: Math.floor(position.y / TILE_SIZE),
  };
}

function screenFromTile(tile: Position): Position {
  return {
    x: tile.x * TILE_SIZE,
    y: tile.y * TILE_SIZE,
  };
}

function isTileCenter(position: Position): boolean {
  return position.x % TILE_SIZE === 0 && position.y % TILE_SIZE === 0;
}

function isValidTile(tile: Position): boolean {
  return tile.x >= 0 && tile.x < BOARD_TILES_WIDTH && tile.y >= 0 && tile.y < BOARD_TILES_HEIGHT;
}

function isWayFree(tile: Position, allowDoor = false): boolean {
  if (!isValidTile(tile)) {
    return false;
  }

  const tileId = waysMatrix[tile.y][tile.x];
  return tileId === WAY_FREE_ID || (allowDoor && tileId === BOX_DOOR_ID);
}

function moveTile(tile: Position, direction: Direction, steps = 1): Position {
  const vector = directionVector(direction);
  return {
    x: tile.x + vector.x * steps,
    y: tile.y + vector.y * steps,
  };
}

function directionVector(direction: Direction): Position {
  switch (direction) {
    case 'UP':
      return { x: 0, y: -1 };
    case 'DOWN':
      return { x: 0, y: 1 };
    case 'LEFT':
      return { x: -1, y: 0 };
    case 'RIGHT':
      return { x: 1, y: 0 };
    default:
      return { x: 0, y: 0 };
  }
}

function oppositeDirection(direction: Direction): Direction {
  switch (direction) {
    case 'UP':
      return 'DOWN';
    case 'DOWN':
      return 'UP';
    case 'LEFT':
      return 'RIGHT';
    case 'RIGHT':
      return 'LEFT';
    default:
      return 'LEFT';
  }
}

function manhattanDistance(from: Position, to: Position): number {
  return Math.abs(from.x - to.x) + Math.abs(from.y - to.y);
}

function wrapHorizontal(position: Position): Position {
  if (position.x < 0) {
    return { ...position, x: BOARD_WIDTH - TILE_SIZE };
  }
  if (position.x >= BOARD_WIDTH) {
    return { ...position, x: 0 };
  }
  return position;
}

function chooseDirectionTowardTarget(currentTile: Position, currentDirection: Direction, targetTile: Position, allowDoor: boolean): Direction {
  const directions: Direction[] = ['UP', 'LEFT', 'DOWN', 'RIGHT'];
  const filtered = directions.filter((direction) => {
    if (direction === oppositeDirection(currentDirection)) {
      return false;
    }
    return isWayFree(moveTile(currentTile, direction), allowDoor);
  });

  const candidates = filtered.length > 0 ? filtered : directions.filter((direction) => isWayFree(moveTile(currentTile, direction), allowDoor));

  let bestDirection = currentDirection;
  let bestDistance = Number.POSITIVE_INFINITY;

  for (const direction of candidates) {
    const nextTile = moveTile(currentTile, direction);
    const distance = manhattanDistance(nextTile, targetTile);
    if (distance < bestDistance) {
      bestDistance = distance;
      bestDirection = direction;
    }
  }

  return bestDirection;
}

function chooseRandomDirection(currentTile: Position, currentDirection: Direction, allowDoor: boolean): Direction {
  const directions: Direction[] = ['UP', 'LEFT', 'DOWN', 'RIGHT'];
  const candidates = directions.filter((direction) => {
    if (direction === oppositeDirection(currentDirection)) {
      return false;
    }
    return isWayFree(moveTile(currentTile, direction), allowDoor);
  });

  const fallback = directions.filter((direction) => isWayFree(moveTile(currentTile, direction), allowDoor));
  const pool = candidates.length > 0 ? candidates : fallback;
  if (pool.length === 0) {
    return currentDirection;
  }

  return pool[Math.floor(Math.random() * pool.length)] ?? currentDirection;
}

function getGhostScatterTarget(ghostId: number): Position {
  switch (ghostId) {
    case 0:
      return { x: 26, y: 1 };
    case 1:
      return { x: 1, y: 1 };
    case 2:
      return { x: 26, y: 29 };
    case 3:
      return { x: 1, y: 29 };
    default:
      return { x: 26, y: 1 };
  }
}

function getGhostTargetTile(ghost: Ghost, game: GameState): Position {
  const pacmanTile = tileFromScreen(game.pacman.position);
  const blinkyTile = tileFromScreen(game.ghosts[0]?.position ?? screenFromTile(HOME_TILE));

  if (ghost.state === 'dead') {
    return HOME_TILE;
  }

  if (ghost.state === 'frightened') {
    return moveTile(tileFromScreen(ghost.position), chooseRandomDirection(tileFromScreen(ghost.position), ghost.direction, true));
  }

  if (game.mode === 'scatter') {
    return ghost.scatterTarget;
  }

  switch (ghost.id) {
    case 0:
      return pacmanTile;
    case 1: {
      const fourTilesAhead = moveTile(pacmanTile, game.pacman.direction, 4);
      return game.pacman.direction === 'UP'
        ? moveTile(fourTilesAhead, 'LEFT', 4)
        : fourTilesAhead;
    }
    case 2: {
      const twoTilesAhead = moveTile(pacmanTile, game.pacman.direction, 2);
      const targetVector = {
        x: twoTilesAhead.x - blinkyTile.x,
        y: twoTilesAhead.y - blinkyTile.y,
      };
      return {
        x: twoTilesAhead.x + targetVector.x,
        y: twoTilesAhead.y + targetVector.y,
      };
    }
    case 3: {
      const distance = manhattanDistance(tileFromScreen(ghost.position), pacmanTile);
      return distance >= 8 ? pacmanTile : ghost.scatterTarget;
    }
    default:
      return pacmanTile;
  }
}

function createGhosts(): Ghost[] {
  return [
    {
      id: 0,
      position: screenFromTile({ x: 12, y: 14 }),
      direction: 'LEFT',
      state: 'scatter',
      previousMode: 'scatter',
      color: '#ff0000',
      scatterTarget: getGhostScatterTarget(0),
    },
    {
      id: 1,
      position: screenFromTile({ x: 13, y: 14 }),
      direction: 'RIGHT',
      state: 'scatter',
      previousMode: 'scatter',
      color: '#ffb9ff',
      scatterTarget: getGhostScatterTarget(1),
    },
    {
      id: 2,
      position: screenFromTile({ x: 14, y: 14 }),
      direction: 'LEFT',
      state: 'scatter',
      previousMode: 'scatter',
      color: '#00ffff',
      scatterTarget: getGhostScatterTarget(2),
    },
    {
      id: 3,
      position: screenFromTile({ x: 15, y: 14 }),
      direction: 'RIGHT',
      state: 'scatter',
      previousMode: 'scatter',
      color: '#ffa500',
      scatterTarget: getGhostScatterTarget(3),
    },
  ];
}

function createInitialGame(): GameState {
  const pills = cloneMatrix(initialPillsMatrix);
  return {
    started: false,
    paused: false,
    gameOver: false,
    gameWon: false,
    score: 0,
    lives: 3,
    powerModeRemaining: 0,
    mode: 'scatter',
    modeTimer: SCATTER_DURATION,
    animationTick: 0,
    spawnProtectionMs: SPAWN_PROTECTION_DURATION,
    pacman: {
      position: screenFromTile({ x: 14, y: 23 }),
      direction: 'LEFT',
      nextDirection: 'LEFT',
    },
    ghosts: createGhosts(),
    pills,
    pelletsLeft: countPellets(pills),
  };
}

function moveEntity(position: Position, direction: Direction, step: number): Position {
  const vector = directionVector(direction);
  return wrapHorizontal({
    x: position.x + vector.x * step,
    y: position.y + vector.y * step,
  });
}

function isCollision(a: Position, b: Position): boolean {
  const ax = a.x + 10;
  const ay = a.y + 10;
  const bx = b.x + 10;
  const by = b.y + 10;
  return Math.abs(ax - bx) < 10 && Math.abs(ay - by) < 10;
}

function updatePacMan(game: GameState): GameState {
  let nextGame = game;
  const pacmanTile = tileFromScreen(nextGame.pacman.position);

  if (isTileCenter(nextGame.pacman.position)) {
    const desiredTile = moveTile(pacmanTile, nextGame.pacman.nextDirection);
    if (isWayFree(desiredTile, false)) {
      nextGame = {
        ...nextGame,
        pacman: {
          ...nextGame.pacman,
          direction: nextGame.pacman.nextDirection,
        },
      };
    }
  }

  const currentTile = tileFromScreen(nextGame.pacman.position);
  const nextTile = moveTile(currentTile, nextGame.pacman.direction);
  if (isWayFree(nextTile, false)) {
    nextGame = {
      ...nextGame,
      pacman: {
        ...nextGame.pacman,
        position: moveEntity(nextGame.pacman.position, nextGame.pacman.direction, PACMAN_STEP),
      },
    };
  }

  const wrappedPosition = wrapHorizontal(nextGame.pacman.position);
  if (wrappedPosition !== nextGame.pacman.position) {
    nextGame = {
      ...nextGame,
      pacman: {
        ...nextGame.pacman,
        position: wrappedPosition,
      },
    };
  }

  const tile = tileFromScreen(nextGame.pacman.position);
  const pillId = nextGame.pills[tile.y]?.[tile.x] ?? 0;
  if (pillId === BASIC_PILL_ID || pillId === ENERGIZER_ID) {
    const updatedPills = cloneMatrix(nextGame.pills);
    updatedPills[tile.y][tile.x] = 0;

    nextGame = {
      ...nextGame,
      pills: updatedPills,
      pelletsLeft: Math.max(0, nextGame.pelletsLeft - 1),
      score: nextGame.score + (pillId === ENERGIZER_ID ? 50 : 10),
    };

    if (pillId === ENERGIZER_ID) {
      nextGame = {
        ...nextGame,
        powerModeRemaining: POWER_MODE_DURATION,
        ghosts: nextGame.ghosts.map((ghost) => {
          if (ghost.state === 'dead') {
            return ghost;
          }
          return {
            ...ghost,
            previousMode: ghost.state === 'frightened' ? ghost.previousMode : nextGame.mode,
            state: 'frightened',
          };
        }),
      };
    }
  }

  return nextGame;
}

function updateGhosts(game: GameState): GameState {
  let nextGame = game;

  const updatedGhosts = nextGame.ghosts.map((ghost, index) => {
    let nextGhost = ghost;
    const ghostTile = tileFromScreen(nextGhost.position);

    const currentMode = nextGame.mode;

    if (nextGhost.state === 'dead' && ghostTile.x === HOME_TILE.x && ghostTile.y === HOME_TILE.y) {
      return {
        ...nextGhost,
        state: currentMode,
      };
    }

    if (isTileCenter(nextGhost.position)) {
      if (nextGhost.state === 'frightened') {
        nextGhost = {
          ...nextGhost,
          direction: chooseRandomDirection(ghostTile, nextGhost.direction, true),
        };
      } else {
        const targetTile = getGhostTargetTile(nextGhost, nextGame);
        nextGhost = {
          ...nextGhost,
          direction: chooseDirectionTowardTarget(ghostTile, nextGhost.direction, targetTile, nextGhost.state === 'dead'),
        };
      }
    }

    const step = nextGhost.state === 'dead'
      ? GHOST_STEP_DEAD
      : nextGhost.state === 'frightened'
        ? GHOST_STEP_FRIGHTENED
        : GHOST_STEP_NORMAL;

    const allowDoor = nextGhost.state === 'dead';
    const nextTile = moveTile(tileFromScreen(nextGhost.position), nextGhost.direction);
    if (isWayFree(nextTile, allowDoor)) {
      nextGhost = {
        ...nextGhost,
        position: moveEntity(nextGhost.position, nextGhost.direction, step),
      };
    }

    return nextGhost;
  });

  nextGame = {
    ...nextGame,
    ghosts: updatedGhosts,
  };

  for (const ghost of nextGame.ghosts) {
    if (!isCollision(nextGame.pacman.position, ghost.position)) {
      continue;
    }

    // Give players a short grace window after spawn to avoid immediate chain deaths.
    if (nextGame.spawnProtectionMs > 0 && ghost.state !== 'dead') {
      continue;
    }

    if (ghost.state === 'frightened') {
      nextGame = {
        ...nextGame,
        score: nextGame.score + 200,
        ghosts: nextGame.ghosts.map((item) => {
          if (item.id !== ghost.id) {
            return item;
          }
          return {
            ...item,
            state: 'dead',
            direction: 'UP',
          };
        }),
      };
    } else if (ghost.state !== 'dead') {
      const remainingLives = nextGame.lives - 1;
      if (remainingLives <= 0) {
        nextGame = {
          ...nextGame,
          lives: 0,
          gameOver: true,
        };
      } else {
        nextGame = {
          ...createInitialGame(),
          started: true,
          lives: remainingLives,
          score: nextGame.score,
          mode: nextGame.mode,
          modeTimer: nextGame.modeTimer,
          animationTick: nextGame.animationTick,
          spawnProtectionMs: SPAWN_PROTECTION_DURATION,
          powerModeRemaining: 0,
        };
        nextGame.pills = cloneMatrix(game.pills);
        nextGame.pelletsLeft = game.pelletsLeft;
        nextGame.score = game.score;
      }
      break;
    }
  }

  return nextGame;
}

function updateModeTimers(game: GameState): GameState {
  let nextGame = game;

  if (nextGame.powerModeRemaining > 0) {
    const remaining = nextGame.powerModeRemaining - STEP_MS;
    if (remaining <= 0) {
      nextGame = {
        ...nextGame,
        powerModeRemaining: 0,
        ghosts: nextGame.ghosts.map((ghost) => {
          if (ghost.state !== 'frightened') {
            return ghost;
          }
          return {
            ...ghost,
            state: nextGame.mode,
          };
        }),
      };
    } else {
      nextGame = {
        ...nextGame,
        powerModeRemaining: remaining,
      };
    }
  }

  const modeTimer = nextGame.modeTimer - STEP_MS;
  if (modeTimer <= 0) {
    const nextMode: GhostMode = nextGame.mode === 'scatter' ? 'chase' : 'scatter';
    nextGame = {
      ...nextGame,
      mode: nextMode,
      modeTimer: nextMode === 'scatter' ? SCATTER_DURATION : CHASE_DURATION,
      ghosts: nextGame.ghosts.map((ghost) => {
        if (ghost.state === 'frightened' || ghost.state === 'dead') {
          return ghost;
        }
        return {
          ...ghost,
          state: nextMode,
        };
      }),
    };
  } else {
    nextGame = {
      ...nextGame,
      modeTimer,
    };
  }

  return nextGame;
}

function advanceGameStep(game: GameState): GameState {
  if (!game.started || game.paused || game.gameOver || game.gameWon) {
    return game;
  }

  let nextGame = updateModeTimers(game);
  nextGame = updatePacMan(nextGame);
  nextGame = updateGhosts(nextGame);
  nextGame = {
    ...nextGame,
    animationTick: nextGame.animationTick + 1,
    spawnProtectionMs: Math.max(0, nextGame.spawnProtectionMs - STEP_MS),
  };

  if (nextGame.pelletsLeft <= 0) {
    nextGame = {
      ...nextGame,
      gameWon: true,
      paused: true,
    };
  }

  return nextGame;
}

function SpriteBox({ x, y, width, height, backgroundPosition, className, opacity = 1 }: {
  x: number;
  y: number;
  width: number;
  height: number;
  backgroundPosition: string;
  className?: string;
  opacity?: number;
}) {
  const style: CSSProperties = {
    position: 'absolute',
    left: x,
    top: y,
    width,
    height,
    opacity,
    backgroundImage: `url(${spriteSheet})`,
    backgroundPosition,
    backgroundRepeat: 'no-repeat',
    transform: `scale(${SPRITE_SCALE})`,
    transformOrigin: 'top left',
    imageRendering: 'pixelated',
    pointerEvents: 'none',
  };

  return <div className={className} style={style} />;
}

function MazeSprite() {
  return (
    <SpriteBox
      x={0}
      y={0}
      width={224}
      height={248}
      backgroundPosition={'calc(-1px * 228) calc(-1px * 0)'}
      className="pacman-sprite pacman-maze"
    />
  );
}

function PillSprite({ x, y, energizer = false }: { x: number; y: number; energizer?: boolean }) {
  return (
    <SpriteBox
      x={x}
      y={y}
      width={8}
      height={8}
      backgroundPosition={energizer ? 'calc(-1px * 8) calc(-1px * 24)' : 'calc(-1px * 8) calc(-1px * 8)'}
      className={energizer ? 'pacman-sprite pacman-energizer' : 'pacman-sprite pacman-pill'}
    />
  );
}

function PacManSprite({ position, direction, frame }: { position: Position; direction: Direction; frame: number }) {
  const phases = [0, 1, 2, 1] as const;
  const phase = phases[Math.floor(frame / 6) % phases.length];
  const backgroundPositionMap: Record<Direction, string[]> = {
    RIGHT: ['calc(-1px * (456 + 0 * 16)) calc(-1px * 0)', 'calc(-1px * (456 + 1 * 16)) calc(-1px * 0)', 'calc(-1px * (456 + 2 * 16)) calc(-1px * 0)'],
    LEFT: ['calc(-1px * (456 + 0 * 16)) calc(-1px * (1 * 16))', 'calc(-1px * (456 + 1 * 16)) calc(-1px * (1 * 16))', 'calc(-1px * (456 + 2 * 16)) calc(-1px * (1 * 16))'],
    UP: ['calc(-1px * (456 + 0 * 16)) calc(-1px * 2 * 16)', 'calc(-1px * (456 + 1 * 16)) calc(-1px * 2 * 16)', 'calc(-1px * (456 + 2 * 16)) calc(-1px * 2 * 16)'],
    DOWN: ['calc(-1px * (456 + 0 * 16)) calc(-1px * 3 * 16)', 'calc(-1px * (456 + 1 * 16)) calc(-1px * 3 * 16)', 'calc(-1px * (456 + 2 * 16)) calc(-1px * 3 * 16)'],
  };

  const x = position.x + TILE_SIZE / 2 - 18;
  const y = position.y + TILE_SIZE / 2 - 18;

  return (
    <SpriteBox
      x={x}
      y={y}
      width={16}
      height={16}
      backgroundPosition={backgroundPositionMap[direction][phase] ?? backgroundPositionMap.RIGHT[0]}
      className="pacman-sprite pacman-character"
    />
  );
}

function GhostSprite({ ghost, frame, powerModeRemaining }: { ghost: Ghost; frame: number; powerModeRemaining: number }) {
  const animationPhase = Math.floor(frame / 8) % 2;
  const x = ghost.position.x + TILE_SIZE / 2 - 20;
  const y = ghost.position.y + TILE_SIZE / 2 - 20;
  const frightened = ghost.state === 'frightened';
  const dead = ghost.state === 'dead';

  let backgroundPosition = '0 0';
  if (frightened) {
    const isBlinking = powerModeRemaining > 0 && powerModeRemaining < 1500;
    const frightenedPhase = isBlinking && Math.floor(frame / 6) % 2 === 0 ? 1 : 0;
    backgroundPosition = frightenedPhase === 0
      ? 'calc(-1px * (584 + 0 * 16)) calc(-1px * (64 + 0 * 16))'
      : 'calc(-1px * (584 + 1 * 16)) calc(-1px * (64 + 0 * 16))';
  } else if (dead) {
    const directionIndex = ghost.direction;
    const deadMap: Record<Direction, string> = {
      RIGHT: 'calc(-1px * (584 + 0 * 16)) calc(-1px * (64 + 1 * 16))',
      LEFT: 'calc(-1px * (584 + 1 * 16)) calc(-1px * (64 + 1 * 16))',
      UP: 'calc(-1px * (584 + 2 * 16)) calc(-1px * (64 + 1 * 16))',
      DOWN: 'calc(-1px * (584 + 3 * 16)) calc(-1px * (64 + 1 * 16))',
    };
    backgroundPosition = deadMap[directionIndex];
  } else {
    const ghostRow = ghost.id;
    const directionIndex = ghost.direction;
    const frameIndex = animationPhase;
    const normalMap: Record<Direction, string[]> = {
      RIGHT: [
        `calc(-1px * (456 + 0 * 16)) calc(-1px * (64 + ${ghostRow} * 16))`,
        `calc(-1px * (456 + 1 * 16)) calc(-1px * (64 + ${ghostRow} * 16))`,
      ],
      LEFT: [
        `calc(-1px * (456 + 2 * 16)) calc(-1px * (64 + ${ghostRow} * 16))`,
        `calc(-1px * (456 + 3 * 16)) calc(-1px * (64 + ${ghostRow} * 16))`,
      ],
      UP: [
        `calc(-1px * (456 + 4 * 16)) calc(-1px * (64 + ${ghostRow} * 16))`,
        `calc(-1px * (456 + 5 * 16)) calc(-1px * (64 + ${ghostRow} * 16))`,
      ],
      DOWN: [
        `calc(-1px * (456 + 6 * 16)) calc(-1px * (64 + ${ghostRow} * 16))`,
        `calc(-1px * (456 + 7 * 16)) calc(-1px * (64 + ${ghostRow} * 16))`,
      ],
    };
    backgroundPosition = normalMap[directionIndex][frameIndex] ?? normalMap.RIGHT[0];
  }

  return (
    <SpriteBox
      x={x}
      y={y}
      width={16}
      height={16}
      backgroundPosition={backgroundPosition}
      className="pacman-sprite pacman-ghost"
    />
  );
}

function PacManGameImpl({ onClose, onReload, onLeaderboardUpdated }: PacManGameProps) {
  const { t } = useTranslation();
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const frameRef = useRef<number | null>(null);
  const lastTimeRef = useRef<number>(0);
  const accumulatorRef = useRef(0);
  const gameStateRef = useRef<GameState>(createInitialGame());
  const leaderboardSubmittedRef = useRef(false);

  const [game, setGame] = useState<GameState>(() => createInitialGame());
  const [isPlaying, setIsPlaying] = useState(false);
  const [showMobileControls, setShowMobileControls] = useState(false);
  const [showNameModal, setShowNameModal] = useState(false);
  const [scoreNameError, setScoreNameError] = useState<string | null>(null);
  const [isSubmittingScore, setIsSubmittingScore] = useState(false);
  const [nameDefault, setNameDefault] = useState(getSavedPlayerName());
  const [highScore, setHighScore] = useState(() => {
    return parseInt(localStorage.getItem('pacmanHighScore') || '0', 10);
  });

  useEffect(() => {
    gameStateRef.current = game;
  }, [game]);

  const startGame = useCallback(() => {
    leaderboardSubmittedRef.current = false;
    setShowNameModal(false);
    setScoreNameError(null);
    const nextGame = createInitialGame();
    nextGame.started = true;
    setGame(nextGame);
    gameStateRef.current = nextGame;
    if (isPlaying && audioRef.current) {
      audioRef.current.play().catch(() => {});
    }
  }, [isPlaying]);

  useEffect(() => {
    const handleResize = () => {
      setShowMobileControls(window.innerWidth <= 900);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const audio = new Audio(audioFile);
    audio.loop = true;
    audioRef.current = audio;
    return () => {
      audio.pause();
      audio.currentTime = 0;
    };
  }, []);

  useEffect(() => {
    if (!audioRef.current) {
      return;
    }
    if (isPlaying && game.started) {
      audioRef.current.play().catch(() => {});
    } else {
      audioRef.current.pause();
    }
  }, [isPlaying, game.started]);

  const toggleMusic = () => {
    setIsPlaying((prev) => !prev);
  };

  const restartGame = useCallback(() => {
    startGame();
  }, [startGame]);

  const handleScoreSubmit = useCallback(async (name: string) => {
    const error = getPlayerNameValidationMessage(name);
    if (error) {
      setScoreNameError(error);
      return;
    }

    setIsSubmittingScore(true);
    setScoreNameError(null);

    try {
      const currentScore = gameStateRef.current.score;
      const submitted = await submitGameHighscore('pacman', name, currentScore);
      if (submitted) {
        leaderboardSubmittedRef.current = true;
        setShowNameModal(false);
        if (currentScore > highScore) {
          setHighScore(currentScore);
          localStorage.setItem('pacmanHighScore', currentScore.toString());
        }
        onLeaderboardUpdated?.();
      } else {
        setScoreNameError('leaderboardModal.errorOnlineUnavailable');
      }
    } catch (error) {
      console.error('Pac-Man leaderboard submit failed:', error);
      setScoreNameError('leaderboardModal.errorSubmit');
    } finally {
      setIsSubmittingScore(false);
    }
  }, [highScore, onLeaderboardUpdated]);

  const handleScoreSkip = useCallback(() => {
    leaderboardSubmittedRef.current = true;
    setShowNameModal(false);
    setScoreNameError(null);
  }, []);

  useEffect(() => {
    const gameOver = game.gameOver || game.gameWon;
    if (!game.started || gameOver || leaderboardSubmittedRef.current || game.score <= 0) {
      return;
    }

    if (gameOver) {
      setNameDefault(getSavedPlayerName());
      setScoreNameError(null);
      setShowNameModal(true);
    }
  }, [game]);

  useEffect(() => {
    const hasFinishedRun = (game.gameOver || game.gameWon) && game.started;
    if (!hasFinishedRun || leaderboardSubmittedRef.current || game.score <= 0) {
      return;
    }

    setNameDefault(getSavedPlayerName());
    setScoreNameError(null);
    setShowNameModal(true);
  }, [game.gameOver, game.gameWon, game.score, game.started]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const currentGame = gameStateRef.current;
      if (!currentGame.started) {
        return;
      }

      const key = event.key.toLowerCase();
      if (key === 'arrowup' || key === 'w') {
        event.preventDefault();
        setGame((prev) => ({ ...prev, pacman: { ...prev.pacman, nextDirection: 'UP' } }));
        return;
      }
      if (key === 'arrowdown' || key === 's') {
        event.preventDefault();
        setGame((prev) => ({ ...prev, pacman: { ...prev.pacman, nextDirection: 'DOWN' } }));
        return;
      }
      if (key === 'arrowleft' || key === 'a') {
        event.preventDefault();
        setGame((prev) => ({ ...prev, pacman: { ...prev.pacman, nextDirection: 'LEFT' } }));
        return;
      }
      if (key === 'arrowright' || key === 'd') {
        event.preventDefault();
        setGame((prev) => ({ ...prev, pacman: { ...prev.pacman, nextDirection: 'RIGHT' } }));
        return;
      }
      if (key === ' ' || key === 'p') {
        event.preventDefault();
        setGame((prev) => ({ ...prev, paused: !prev.paused }));
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    const loop = (time: number) => {
      if (!lastTimeRef.current) {
        lastTimeRef.current = time;
      }

      accumulatorRef.current += time - lastTimeRef.current;
      lastTimeRef.current = time;

      let steps = 0;
      while (accumulatorRef.current >= STEP_MS && steps < 5) {
        setGame((prev) => {
          const next = advanceGameStep(prev);
          gameStateRef.current = next;
          return next;
        });
        accumulatorRef.current -= STEP_MS;
        steps += 1;
      }

      frameRef.current = window.requestAnimationFrame(loop);
    };

    if (game.started && !game.paused && !game.gameOver && !game.gameWon) {
      frameRef.current = window.requestAnimationFrame(loop);
    }

    return () => {
      if (frameRef.current !== null) {
        window.cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
      lastTimeRef.current = 0;
      accumulatorRef.current = 0;
    };
  }, [game.started, game.paused, game.gameOver, game.gameWon]);

  useEffect(() => {
    if (!game.started || leaderboardSubmittedRef.current) {
      return;
    }

    if ((game.gameOver || game.gameWon) && game.score > 0) {
      setNameDefault(getSavedPlayerName());
      setScoreNameError(null);
      setShowNameModal(true);
    }
  }, [game.gameOver, game.gameWon, game.score, game.started]);

  const currentPellets = useMemo(() => {
    const items: Array<{ x: number; y: number; energizer: boolean }> = [];
    for (let y = 0; y < game.pills.length; y++) {
      for (let x = 0; x < game.pills[y].length; x++) {
        const tileId = game.pills[y][x];
        if (tileId === BASIC_PILL_ID) {
          items.push({ x: x * TILE_SIZE, y: y * TILE_SIZE, energizer: false });
        } else if (tileId === ENERGIZER_ID) {
          items.push({ x: x * TILE_SIZE, y: y * TILE_SIZE, energizer: true });
        }
      }
    }
    return items;
  }, [game.pills]);

  const renderStartScreen = () => (
    <div className="pacman-start-screen">
      <h2>{t('pacmanGame.title')}</h2>
      <p>{t('pacmanGame.hintKeyboard')}</p>
      <p>{t('pacmanGame.hintSwipe')}</p>
      <div className="pacman-tip-list">
        <div>{t('pacmanGame.tipCollectDots')}</div>
        <div>{t('pacmanGame.tipAvoidRed')}</div>
        <div>{t('pacmanGame.tipPowerPellet')}</div>
        <div>{t('pacmanGame.tipEatVulnerable')}</div>
      </div>
      <button onClick={startGame} className="start-btn">
        {t('gamesCommon.startGame')}
      </button>
    </div>
  );

  return (
    <div className="pacman-container">
      <div className="game-header">
        <div className="controls left">
          <button onClick={toggleMusic} className="music-btn">
            {isPlaying ? '🔊' : '🔇'} {t('gamesCommon.music')}
          </button>
        </div>
        <div className="controls right">
          {onClose && (
            <button onClick={onClose} className="close-button">
              ✕
            </button>
          )}
        </div>
      </div>

      {!game.started ? (
        renderStartScreen()
      ) : (
        <>
          <div className="pacman-board-shell">
            <div className="pacman-board">
              <MazeSprite />
              {currentPellets.map((pellet) => (
                <PillSprite key={`${pellet.x}-${pellet.y}-${pellet.energizer ? 'e' : 'p'}`} x={pellet.x} y={pellet.y} energizer={pellet.energizer} />
              ))}
              <PacManSprite position={game.pacman.position} direction={game.pacman.direction} frame={game.animationTick} />
              {game.ghosts.map((ghost) => (
                <GhostSprite
                  key={ghost.id}
                  ghost={ghost}
                  frame={game.animationTick}
                  powerModeRemaining={game.powerModeRemaining}
                />
              ))}
            </div>
          </div>

          <div className="pacman-stats-row">
            <div>{t('gamesCommon.scoreWithValue', { score: game.score })}</div>
            <div>{t('gamesCommon.highScoreWithValue', { score: Math.max(highScore, game.score) })}</div>
            <div>{t('gamesCommon.livesWithValue', { lives: game.lives })}</div>
            <div>{game.powerModeRemaining > 0 ? t('pacmanGame.powerMode') : t('gamesCommon.levelWithValue', { level: 1 })}</div>
          </div>

          {showMobileControls && (
            <div className="pacman-mobile-controls">
              <div className="pacman-mobile-row">
                <button className="pacman-mobile-btn" onClick={() => setGame((prev) => ({ ...prev, pacman: { ...prev.pacman, nextDirection: 'UP' } }))}>↑</button>
              </div>
              <div className="pacman-mobile-row">
                <button className="pacman-mobile-btn" onClick={() => setGame((prev) => ({ ...prev, pacman: { ...prev.pacman, nextDirection: 'LEFT' } }))}>←</button>
                <button
                  className="pacman-mobile-btn pacman-mobile-btn-pause"
                  onClick={() => setGame((prev) => ({ ...prev, paused: !prev.paused }))}
                >
                  {game.paused ? '▶' : 'II'}
                </button>
                <button className="pacman-mobile-btn" onClick={() => setGame((prev) => ({ ...prev, pacman: { ...prev.pacman, nextDirection: 'RIGHT' } }))}>→</button>
              </div>
              <div className="pacman-mobile-row">
                <button className="pacman-mobile-btn" onClick={() => setGame((prev) => ({ ...prev, pacman: { ...prev.pacman, nextDirection: 'DOWN' } }))}>↓</button>
              </div>
            </div>
          )}

          {game.paused && !game.gameOver && !game.gameWon && (
            <div className="pause-overlay">
              <h2>{t('gamesCommon.paused')}</h2>
              <p>{t('pacmanGame.hintKeyboard')}</p>
            </div>
          )}

          {(game.gameOver || game.gameWon) && (
            <div className={`game-end-overlay ${game.gameWon ? 'won' : 'lost'}`}>
              <h2>{game.gameWon ? t('pacmanGame.levelComplete') : t('gamesCommon.gameOver')}</h2>
              <p>{t('gamesCommon.finalScore', { score: game.score })}</p>
              <div className="overlay-buttons">
                <button onClick={restartGame} className="play-again-btn">
                  {t('gamesCommon.playAgain')}
                </button>
              </div>
            </div>
          )}
        </>
      )}

      <LeaderboardNameModal
        open={showNameModal}
        score={game.score}
        defaultName={nameDefault}
        isSubmitting={isSubmittingScore}
        error={scoreNameError}
        onSubmit={handleScoreSubmit}
        onCancel={handleScoreSkip}
      />
    </div>
  );
}

export default PacManGameImpl;
